package link.erpango.quip.ui.transfer

import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.material.CircularProgressIndicator
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.Scaffold
import androidx.compose.material.Text
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import link.erpango.quip.domain.entities.QRData
import link.erpango.quip.features.transfer.TransferCode
import link.erpango.quip.theme.QuipColors
import link.erpango.quip.ui.components.*

@Composable
fun ScanQRScreen(
    navController: NavController,
    onResult: (String) -> Unit = {},
    onError: (String) -> Unit = {},
    onNavigateBack: () -> Unit = {}
) {
    var qrPayload by remember { mutableStateOf("") }
    var localError by remember { mutableStateOf<String?>(null) }
    // Flag to prevent multiple scans after first successful detection
    var scanCompleted by remember { mutableStateOf(false) }
    
    Scaffold(
        backgroundColor = QuipColors.colorBackground
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Header with back button
            QuipTransparentHeader {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            Icons.Default.ArrowBack,
                            contentDescription = "Volver",
                            tint = QuipColors.colorText
                        )
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = "Escanear QR",
                        style = androidx.compose.material.MaterialTheme.typography.h6,
                        fontWeight = androidx.compose.ui.text.font.FontWeight.Bold,
                        color = QuipColors.colorText
                    )
                }
            }

            // Scrollable content area (scanner + form)
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .imePadding()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Spacer(modifier = Modifier.height(8.dp))

                // Camera-based QR scanner using platform-specific implementation
                PlatformQrScanner.QrScannerView(
                    modifier = Modifier.fillMaxWidth().height(240.dp),
                    onQrScanned = { scannedData ->
                        if (!scanCompleted) {
                            scanCompleted = true

                            val qrData = QRData.fromJson(scannedData)
                            if (qrData != null) {
                                navController.navigate("accept-transfer?token=${qrData.token}")
                            } else {
                                localError = "Código QR no válido para transferencia"
                                onError(localError!!)
                                scanCompleted = false
                            }
                        }
                    },
                    onError = { errorMsg ->
                        if (!scanCompleted) {
                            localError = errorMsg
                            onError(errorMsg)
                        }
                    }
                )

                Spacer(modifier = Modifier.height(16.dp))

                Text(
                    text = "¿No puedes escanear? Introduce el código que aparece bajo el QR del emisor:",
                    style = androidx.compose.material.MaterialTheme.typography.body2,
                    color = QuipColors.colorTextSecondary,
                    modifier = Modifier.fillMaxWidth()
                )

                // Entrada manual: OTP corto (ABCD-2345) o, por compatibilidad, el JSON del QR.
                QuipTextField(
                    value = qrPayload,
                    onValueChange = {
                        qrPayload = TransferCode.formatWhileTyping(it)
                        localError = null
                    },
                    label = "Código de transferencia",
                    placeholder = {
                        Text(
                            text = TransferCode.OTP_EXAMPLE,
                            color = QuipColors.colorTextSecondary,
                            fontFamily = androidx.compose.ui.text.font.FontFamily.Monospace,
                            style = androidx.compose.material.MaterialTheme.typography.body2
                        )
                    },
                    keyboardOptions = KeyboardOptions(
                        capitalization = KeyboardCapitalization.Characters,
                        autoCorrectEnabled = false,
                        keyboardType = KeyboardType.Ascii,
                        imeAction = ImeAction.Done
                    ),
                    modifier = Modifier.fillMaxWidth(),
                    isError = localError != null,
                    helperText = if (localError != null) {
                        { Text(localError ?: "", color = QuipColors.colorError) }
                    } else {
                        {
                            Text(
                                "Formato ${TransferCode.OTP_EXAMPLE}. No distingue mayúsculas, guiones ni espacios.",
                                color = QuipColors.colorTextSecondary
                            )
                        }
                    }
                )

                Spacer(modifier = Modifier.height(12.dp))

                QuipPrimaryButton(
                    text = "Continuar",
                    onClick = {
                        // JSON del QR -> su token; si no, se trata como OTP.
                        val code = TransferCode.resolveManualInput(qrPayload)
                        if (code != null && !scanCompleted) {
                            scanCompleted = true
                            navController.navigate("accept-transfer?token=$code")
                        } else if (!scanCompleted) {
                            val msg = "Introduce el código de transferencia"
                            localError = msg
                            onError(msg)
                        }
                    },
                    enabled = qrPayload.isNotBlank() && !scanCompleted,
                )

                Spacer(modifier = Modifier.height(16.dp))

                // Back button (hidden during processing to prevent accidental navigation)
                if (!scanCompleted) {
                    QuipSecondaryButton(
                        text = "Volver",
                        onClick = onNavigateBack,
                        icon = Icons.Default.ArrowBack
                    )
                }

                Spacer(modifier = Modifier.height(16.dp))
            }
        }

        // Overlay spinner when scan is completed and navigating
        if (scanCompleted) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                androidx.compose.foundation.layout.Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    modifier = Modifier.padding(32.dp)
                ) {
                    CircularProgressIndicator(color = QuipColors.colorPrimary)
                    Text(
                        text = "Verificando código...",
                        style = androidx.compose.material.MaterialTheme.typography.body1,
                        color = Color.White.copy(alpha = 0.9f)
                    )
                }
            }
        }
    }
}
